import type { AeEvaluatedQuestion } from '@/types/assessmentEngine';
import type { AeDemographicFieldKey } from '@/components/assessment-engine/template-builder/templateConfigTypes';

/** Large-screen row order for About you fields (contact → org → rest). */
export const DEMOGRAPHIC_LAYOUT_ROWS: AeDemographicFieldKey[][] = [
  ['name', 'email', 'phone'],
  ['company_name', 'position', 'title'],
  ['address'],
];

export function isDemographicQuestion(q: AeEvaluatedQuestion): boolean {
  if (q.question_type === 'demographic') return true;
  if (q.question_key.toUpperCase().startsWith('DEMO_')) return true;
  const meta = (q as { validation_config?: Record<string, unknown> }).validation_config;
  return typeof meta?.['demographic_field'] === 'string';
}

export function demographicFieldKey(q: AeEvaluatedQuestion): AeDemographicFieldKey | null {
  const meta = (q as { validation_config?: Record<string, unknown> }).validation_config;
  const fromMeta = meta?.['demographic_field'];
  if (
    typeof fromMeta === 'string' &&
    ['name', 'email', 'phone', 'address', 'title', 'company_name', 'position'].includes(fromMeta)
  ) {
    return fromMeta as AeDemographicFieldKey;
  }
  const m = q.question_key.match(/^DEMO_(.+)$/i);
  if (!m?.[1]) return null;
  const key = m[1].toLowerCase();
  if (key === 'company_name' || key === 'company') return 'company_name';
  if (['name', 'email', 'phone', 'address', 'title', 'position'].includes(key)) {
    return key as AeDemographicFieldKey;
  }
  return null;
}

/**
 * Group demographic questions into layout rows for compact grids.
 * Unknown fields append to the last row.
 */
export function groupDemographicQuestions(
  questions: AeEvaluatedQuestion[]
): AeEvaluatedQuestion[][] {
  const byKey = new Map<AeDemographicFieldKey, AeEvaluatedQuestion>();
  const unknown: AeEvaluatedQuestion[] = [];
  for (const q of questions) {
    const key = demographicFieldKey(q);
    if (key) byKey.set(key, q);
    else unknown.push(q);
  }

  const rows: AeEvaluatedQuestion[][] = [];
  for (const keys of DEMOGRAPHIC_LAYOUT_ROWS) {
    const row = keys.map((k) => byKey.get(k)).filter((q): q is AeEvaluatedQuestion => !!q);
    if (row.length > 0) rows.push(row);
  }
  if (unknown.length > 0) {
    if (rows.length === 0) rows.push(unknown);
    else rows[rows.length - 1] = [...(rows[rows.length - 1] ?? []), ...unknown];
  }
  return rows;
}

/** True when every visible question in the list is a demographic field. */
export function isDemographicsQuestionList(questions: AeEvaluatedQuestion[]): boolean {
  return questions.length > 0 && questions.every(isDemographicQuestion);
}

/** Inclusive index range of contiguous demographic questions containing `index`. */
export function contiguousDemographicRange(
  questions: AeEvaluatedQuestion[],
  index: number
): { start: number; end: number } | null {
  const current = questions[index];
  if (!current || !isDemographicQuestion(current)) return null;
  let start = index;
  while (start > 0 && isDemographicQuestion(questions[start - 1]!)) start -= 1;
  let end = index;
  while (end + 1 < questions.length && isDemographicQuestion(questions[end + 1]!)) end += 1;
  return { start, end };
}
